import * as React from 'react'
import Box from '@mui/material/Box'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { alpha, useTheme } from '@mui/material/styles'
import type { CalendarView } from '../../typings'
import { COARSE_POINTER, TOUCH_TARGET_PX } from '../touch'

type Props = {
  view: CalendarView
  onChange: (view: CalendarView) => void
}

// M2 ships all three views — every toggle is selectable.
const OPTIONS: CalendarView[] = ['month', 'week', 'day']

export function ViewToggle({ view, onChange }: Props) {
  const theme = useTheme()

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: CalendarView | null,
  ) => {
    // Exclusive groups emit `null` when the active button is re-clicked; ignore
    // it so a view is always selected.
    if (value) onChange(value)
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        borderRadius: '8px',
        padding: '2px',
        display: 'inline-flex',
      }}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={view}
        onChange={handleChange}
        aria-label="Calendar view"
      >
        {OPTIONS.map((value) => (
          <ToggleButton
            key={value}
            value={value}
            aria-label={value}
            // 44px touch target on touch devices (story AC), compact on desktop.
            // Selected stays white on hover; MUI would otherwise darken it and
            // read as deselecting.
            sx={{
              border: 'none',
              borderRadius: '6px',
              padding: '5px 16px',
              minHeight: { xs: `${TOUCH_TARGET_PX}px`, lg: 0 },
              [COARSE_POINTER]: { minHeight: `${TOUCH_TARGET_PX}px` },
              fontFamily: theme.typography.fontFamily,
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'capitalize',
              color: theme.palette.text.secondary,
              '&.Mui-selected': {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.primary.main,
                boxShadow: `0 1px 2px ${alpha(theme.palette.primary.main, 0.1)}`,
              },
              '&.Mui-selected:hover': {
                backgroundColor: theme.palette.background.paper,
              },
            }}
          >
            {value}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  )
}
